import { isWindows } from 'react-device-detect';

import { logger } from 'common/services';

import type { MediaDevice } from 'devices/types';

type DeviceSelectionReason =
  | 'user-selected-available'
  | 'user-selected-unavailable-fallback-to-first'
  | 'no-preference-using-first'
  | 'no-devices-available';

/**
 * Device selection logic following industry best practices from Google Meet, Teams, and Zoom.
 *
 * Priority hierarchy:
 * 1. User's explicitly selected device (if available)
 * 2. First available device of the matching kind
 *
 * Key principles:
 * - User selection is sacred: honor user's choice regardless of changes to available devices
 * - Only fall back when device is physically unavailable (not in device list)
 * - Track selection reasons for telemetry and debugging
 */
export const getInitialDeviceId = (
  mediaDevices: MediaDevice[],
  kind: MediaDeviceKind
): { deviceId: string; reason: DeviceSelectionReason } => {
  // Priority 1: Check if user has explicitly selected a device
  const storedSelectedDeviceId = localStorage.getItem(getDeviceLocalStorageKey(kind, 'selected'));

  // On Windows, ignore the "communications" device if it was previously stored
  // This handles legacy localStorage entries and ensures consistency with UI filtering
  const shouldIgnoreStoredDevice = isWindows && storedSelectedDeviceId === 'communications';

  if (storedSelectedDeviceId && !shouldIgnoreStoredDevice) {
    const selectedDevice = mediaDevices.find((device) => device.deviceId === storedSelectedDeviceId);

    if (selectedDevice) {
      // User's selected device is still available - use it
      logger.info('[getInitialDeviceId] Using user-selected device', {
        extra: { kind, deviceId: selectedDevice.deviceId, label: selectedDevice.label },
      });
      return { deviceId: selectedDevice.deviceId, reason: 'user-selected-available' };
    }
    // User's selected device was unplugged/removed - continue to fallback
  }

  // Priority 2: Fall back to first available device (or no user preference set)
  const firstAvailableDevice = mediaDevices.find((device) => device.kind === kind);

  if (firstAvailableDevice) {
    const reason =
      storedSelectedDeviceId && !shouldIgnoreStoredDevice
        ? 'user-selected-unavailable-fallback-to-first'
        : 'no-preference-using-first';

    logger.info('[getInitialDeviceId] Using first available device', {
      extra: { kind, deviceId: firstAvailableDevice.deviceId, label: firstAvailableDevice.label, reason },
    });

    return { deviceId: firstAvailableDevice.deviceId, reason };
  }

  // No devices of this kind available at all
  logger.error('[getInitialDeviceId] No devices available', { extra: { kind } });
  return { deviceId: '', reason: 'no-devices-available' };
};

export const getDeviceLocalStorageKey = (kind: MediaDeviceKind, type: 'default' | 'selected') =>
  `${kind}-device-${type}`;
